import {
  formatSurfaceTotals,
  LABOUR_PRICING_METHOD,
  workTypeLabel,
} from "./estimates";

export function getMaterialUnitCost(row, materialsCatalog) {
  if (row.material_id == null) {
    return Number(row.unit_cost_snapshot) || 0;
  }

  const catalogItem = materialsCatalog.find(
    (item) => item.material_id === row.material_id,
  );
  return Number(catalogItem?.unit_cost ?? row.unit_cost_snapshot) || 0;
}

export function computeMaterialLineTotal(row, materialsCatalog) {
  const quantity =
    row.quantity != null && row.quantity !== ""
      ? Number(row.quantity) || 0
      : 0;
  return quantity * getMaterialUnitCost(row, materialsCatalog);
}

export function computeWorkTypeMaterialsTotal(workType, materialsCatalog) {
  const materials = workType?.materials || [];
  return materials.reduce(
    (sum, row) => sum + computeMaterialLineTotal(row, materialsCatalog),
    0,
  );
}

export function computeWorkTypeTotalSqft(workType) {
  const totals = formatSurfaceTotals(workType?.surface_totals);
  return totals.reduce((sum, row) => sum + row.sqft, 0);
}

function getLabourUnitRate(row) {
  return row.hourly_rate == null || row.hourly_rate === ""
    ? 0
    : Number(row.hourly_rate) || 0;
}

export function computeLabourLineTotal(row, workType = null) {
  const method = row.pricing_method || LABOUR_PRICING_METHOD.HOURLY;
  const rate = getLabourUnitRate(row);

  if (method === LABOUR_PRICING_METHOD.SQUARE_FOOT) {
    const sqft = workType ? computeWorkTypeTotalSqft(workType) : 0;
    return sqft * rate;
  }

  const hours = Number(row.estimated_hours) || 0;
  return hours * rate;
}

export function formatLabourLineDetail(row, workType, { showPricing = true } = {}) {
  const method = row.pricing_method || LABOUR_PRICING_METHOD.HOURLY;
  const hasRate = row.hourly_rate != null && row.hourly_rate !== "";

  if (method === LABOUR_PRICING_METHOD.SQUARE_FOOT) {
    const sqft = workType ? computeWorkTypeTotalSqft(workType) : 0;
    const sqftLabel = `${sqft.toFixed(1)} sqft`;
    if (showPricing && hasRate) {
      return `${sqftLabel} × ${formatCurrency(row.hourly_rate)}/sqft`;
    }
    return sqftLabel;
  }

  const hours = Number(row.estimated_hours);
  if (showPricing && hasRate) {
    return `${hours} hrs × ${formatCurrency(row.hourly_rate)}/hr`;
  }
  return `${hours} hrs`;
}

export function computeWorkTypeLabourTotal(workType) {
  const labour = workType?.labour || [];
  return labour.reduce(
    (sum, row) => sum + computeLabourLineTotal(row, workType),
    0,
  );
}

export function computeWorkTypeTotal(workType, materialsCatalog) {
  return (
    computeWorkTypeMaterialsTotal(workType, materialsCatalog) +
    computeWorkTypeLabourTotal(workType)
  );
}

export function computeLocationTotal(location, materialsCatalog) {
  const workTypes = location?.workTypes || [];
  return workTypes.reduce(
    (sum, wt) => sum + computeWorkTypeTotal(wt, materialsCatalog),
    0,
  );
}

export function computeEstimateTotals(estimateTree, materialsCatalog) {
  const locations = (estimateTree?.locations || []).map((location) => {
    const workTypes = (location.workTypes || []).map((workType) => {
      const materialsTotal = computeWorkTypeMaterialsTotal(
        workType,
        materialsCatalog,
      );
      const labourTotal = computeWorkTypeLabourTotal(workType);
      const workTypeTotal = materialsTotal + labourTotal;
      const label = workTypeLabel(workType);

      return {
        workTypeId: workType.estimate_feature_id,
        label,
        materialsTotal,
        labourTotal,
        workTypeTotal,
      };
    });

    const locationTotal = workTypes.reduce(
      (sum, wt) => sum + wt.workTypeTotal,
      0,
    );

    return {
      locationId: location.room_id,
      locationName: location.name,
      isDefaultScope: location.is_default_scope === true,
      workTypes,
      locationTotal,
    };
  });

  const calculatedMaterialsTotal = locations.reduce(
    (sum, loc) =>
      sum + loc.workTypes.reduce((s, wt) => s + wt.materialsTotal, 0),
    0,
  );
  const calculatedLabourTotal = locations.reduce(
    (sum, loc) =>
      sum + loc.workTypes.reduce((s, wt) => s + wt.labourTotal, 0),
    0,
  );
  const calculatedEstimateTotal =
    calculatedMaterialsTotal + calculatedLabourTotal;

  const wasteAdjustment = Number(estimateTree?.waste_adjustment) || 0;
  const travelAdjustment = Number(estimateTree?.travel_adjustment) || 0;
  const miscAdjustment = Number(estimateTree?.misc_adjustment) || 0;
  const adjustedTotal =
    calculatedEstimateTotal +
    wasteAdjustment +
    travelAdjustment +
    miscAdjustment;

  const storedFinal = estimateTree?.final_estimate_price;
  const finalEstimatePrice =
    storedFinal == null || storedFinal === ""
      ? null
      : Number(storedFinal);

  return {
    locations,
    calculatedMaterialsTotal,
    calculatedLabourTotal,
    calculatedEstimateTotal,
    wasteAdjustment,
    travelAdjustment,
    miscAdjustment,
    adjustedTotal,
    finalEstimatePrice,
  };
}

export function formatCurrency(amount) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  return `$${safe.toFixed(2)}`;
}
